import { useState, forwardRef } from "react";

const FinanceCalculator = forwardRef(
  ({ defaultDeposit = 0, carPrice = 0, onRequestFinance, isSold }, ref) => {
    // console.log("onRequestFinance", onRequestFinance);

    const [deposit, setDeposit] = useState(defaultDeposit);
    const [annualMileage, setAnnualMileage] = useState("");
    const [termLength, setTermLength] = useState(48);
    const [creditScore, setCreditScore] = useState("fair");
    const [financeType, setFinanceType] = useState("HP"); // HP or PCP

    const [financeDetails, setFinanceDetails] = useState({
      monthlyPayment: 0,
      totalInterest: 0,
      totalPayable: 0,
      apr: 0,
    });

    // APR rates based on credit score
    const aprRates = {
      excellent: 9.9,
      good: 10.9,
      fair: 11.4,
      poor: 14.9,
      "very-poor": 16.9,
      "not-sure": 11.4,
    };

    // Calculate finance details
    const calculateFinance = () => {
      if (financeType === "PCP") {
        // PCP logic from provided code
        // APR by term
        const aprByTerm = {
          24: 0.119,
          36: 0.085,
          42: 0.085,
          48: 0.085,
        };
        // Balloon value factors
        const balloons = {
          24: { 8000: 0.50, 10000: 0.475, 12000: 0.45, 15000: 0.425 },
          36: { 8000: 0.525, 10000: 0.50, 12000: 0.475, 15000: 0.45 },
          42: { 8000: 0.475, 10000: 0.45, 12000: 0.425, 15000: 0.40 },
          48: { 8000: 0.425, 10000: 0.40, 12000: 0.375, 15000: 0.35 },
        };
        const p = carPrice;
        const d = deposit;
        const t = Number(termLength);
        const m = Number(annualMileage);
        if (!balloons[t] || !balloons[t][m]) {
          return {
            monthlyPayment: 0,
            totalInterest: 0,
            totalPayable: 0,
            apr: 0,
            balloon: 0,
          };
        }
        const APR = aprByTerm[t];
        const r = APR / 12;
        const balloonValue = p * balloons[t][m];
        const pv = balloonValue / Math.pow(1 + r, t);
        const monthlyPayment = ((p - d - pv) * r) / (1 - Math.pow(1 + r, -t));
        // For PCP, total interest and total payable are less relevant, but we can estimate:
        const totalPayable = d + (monthlyPayment * t) + balloonValue;
        const totalInterest = totalPayable - p;
        return {
          monthlyPayment: isNaN(monthlyPayment) ? 0 : monthlyPayment,
          totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
          totalPayable: isNaN(totalPayable) ? 0 : totalPayable,
          apr: (APR * 100).toFixed(1),
          balloon: isNaN(balloonValue) ? 0 : balloonValue,
        };
      } else {
        // HP logic (existing)
        const score = creditScore;
        if (!score) {
          return {
            monthlyPayment: 0,
            totalInterest: 0,
            totalPayable: 0,
            apr: 0,
          };
        }
        const apr = aprRates[score];
        const amountToFinance = carPrice - deposit;
        const termYears = termLength / 12;
        // Calculate simple interest
        const totalInterest = (amountToFinance * apr * termYears) / 100;
        const totalPayable = carPrice + totalInterest;
        const monthlyPayment = (totalPayable - deposit) / termLength;
        return {
          monthlyPayment: monthlyPayment,
          totalInterest: totalInterest,
          totalPayable: totalPayable,
          apr: apr,
        };
      }
    };

    // const financeDetails = calculateFinance();
    const handleCalculate = () => {
      if (financeType === "HP" && !creditScore) {
        alert("Please select a credit score to see results");
        return;
      }
      if (deposit >= carPrice) {
        alert("Please enter correct deposit price");
        return;
      }
      const results = calculateFinance();
      setFinanceDetails(results); // update state
    };

    return (
      <section
        ref={ref}
        className="bg-white p-10 w-full mx-auto border-t-[1px]"
      >
        {/* PCP/HP Toggle Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
          {/* Desktop: pill style, mobile: circular/stacked */}
          <button
            type="button"
            className={`
              hidden sm:inline-flex items-center justify-center px-8 py-3 rounded-full border-2 font-semibold transition text-lg text-center whitespace-nowrap
              ${financeType === "HP"
                ? "bg-accent text-white border-accent"
                : "bg-white text-accent border-accent"}
            `}
            style={{ minWidth: 260 }}
            onClick={() => setFinanceType("HP")}
          >
            Hire Purchase (HP)
          </button>
          <button
            type="button"
            className={`
              hidden sm:inline-flex items-center justify-center px-8 py-3 rounded-full border-2 font-semibold transition text-lg text-center whitespace-nowrap
              ${financeType === "PCP"
                ? "bg-accent text-white border-accent"
                : "bg-white text-accent border-accent"}
            `}
            style={{ minWidth: 340 }}
            onClick={() => setFinanceType("PCP")}
          >
            {financeType === "PCP" && termLength === 24 ? "Lease Purchase" : "Personal Contract Purchase (PCP)"}
          </button>

          {/* Mobile: keep circular stacked style */}
          <button
            type="button"
            className={`sm:hidden w-40 h-40 flex flex-col justify-center items-center rounded-full border-2 font-semibold transition text-2xl text-center whitespace-normal leading-tight
              ${financeType === "HP"
                ? "bg-accent text-white border-accent shadow-lg"
                : "bg-white text-accent border-accent hover:bg-accent/10"}
            `}
            onClick={() => setFinanceType("HP")}
          >
            HP
          </button>
          <button
            type="button"
            className={`sm:hidden w-40 h-40 flex flex-col justify-center items-center rounded-full border-2 font-semibold transition text-2xl text-center whitespace-normal leading-tight
              ${financeType === "PCP"
                ? "bg-accent text-white border-accent shadow-lg"
                : "bg-white text-accent border-accent hover:bg-accent/10"}
            `}
            onClick={() => setFinanceType("PCP")}
          >
            PCP
          </button>
        </div>

        {/* ───── GRID LAYOUT (2 COLUMNS) ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-4">
          {/* LEFT SECTION - INPUTS */}
          <div>
            <h2 className="text-3xl font-bold text-accent mt-4 mb-8">
              Finance calculator
            </h2>


            {/* HP: Cash deposit, PCP: Vehicle Price + Deposit */}
            {financeType === "HP" ? (
              <div className="mb-6">
                <label
                  htmlFor="deposit-hp"
                  className="text-primary font-semibold mb-1 block flex items-center gap-2"
                >
                  Cash deposit
                </label>
                <div className="text-gray-500 text-xs mb-2">
                  A higher deposit could mean a lower monthly payment
                </div>
                <input
                  id="deposit-hp"
                  type="number"
                  min={0}
                  max={carPrice}
                  value={deposit}
                  onChange={(e) => setDeposit(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-base"
                />
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <label
                    htmlFor="vehicle-price"
                    className="text-primary font-semibold mb-1 block flex items-center gap-2"
                  >
                    Vehicle Price (£)
                  </label>
                  <div className="text-gray-500 text-xs mb-2">
                    Please provide the actual price of the vehicle
                  </div>
                  <input
                    id="vehicle-price"
                    type="number"
                    min={0}
                    value={carPrice}
                    readOnly
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-base bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="deposit-pcp"
                    className="text-primary font-semibold mb-1 block flex items-center gap-2"
                  >
                    Deposit (£)
                  </label>
                  <div className="text-gray-500 text-xs mb-2">
                    Enter your deposit amount
                  </div>
                  <input
                    id="deposit-pcp"
                    type="number"
                    min={0}
                    max={carPrice}
                    value={deposit}
                    onChange={(e) => setDeposit(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-base"
                  />
                </div>
              </>
            )}

            {/* Expected annual mileage (conditional) */}
            <div className="mb-6">
              <label
                htmlFor="mileage"
                className="text-primary font-semibold mb-1 block"
              >
                Expected annual mileage
              </label>
              <div className="text-gray-500 text-xs mb-2">
                Average mileage is around 10,000 per year
              </div>
              <select
                id="mileage"
                value={annualMileage}
                onChange={(e) => setAnnualMileage(e.target.value)}
                className="mt-1 block w-full rounded-lg bg-white px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select mileage</option>
                {financeType === "HP"
                  ? Array.from({ length: 25 }, (_, i) => 6000 + i * 1000).map((miles) => (
                      <option key={miles} value={miles}>
                        {miles.toLocaleString()} miles
                      </option>
                    ))
                  : [8000, 10000, 12000, 15000].map((miles) => (
                      <option key={miles} value={miles}>
                        {miles.toLocaleString()} miles
                      </option>
                    ))}
              </select>
            </div>

            {/* Term length (conditional) */}
            <div className="mb-6">
              <label className="text-primary font-semibold mb-1 block">
                Term length
              </label>
              <div className="text-gray-500 text-xs mb-2">
                {financeType === "HP"
                  ? "Most customers choose 48 months"
                  : "PCP available for 24, 36, 42, 48 months"}
              </div>
              <div className="flex gap-3 flex-wrap">
                {(financeType === "HP" ? [36, 42, 48, 60] : [24, 36, 42, 48]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`border rounded-md px-4 py-2 cursor-pointer text-base ${
                      termLength === m
                        ? "border-blue-600 text-blue-600 font-semibold"
                        : "border-gray-300 text-black"
                    }`}
                    onClick={() => setTermLength(m)}
                  >
                    {financeType === "PCP" && m === 24 ? "24 months (Lease Purchase)" : `${m} months`}
                  </button>
                ))}
              </div>
            </div>

            {/* Estimated credit score (only for HP) */}
            {financeType === "HP" && (
              <div className="mb-6">
                <label
                  htmlFor="creditScore"
                  className="text-primary font-semibold mb-1 block"
                >
                  Estimated credit score
                </label>
                <div className="text-gray-500 text-xs mb-2">
                  This helps you get a more accurate finance estimate
                </div>
                <select
                  id="creditScore"
                  value={creditScore}
                  onChange={(e) => setCreditScore(e.target.value)}
                  className="mt-1 block w-full rounded-lg bg-white px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select credit score</option>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="very-poor">Very Poor</option>
                  <option value="not-sure">Not Sure</option>
                </select>
              </div>
            )}

            {/* Credit score info link */}
            {/* <div className="mb-8 flex items-center gap-2 text-blue-700 text-sm cursor-pointer select-none">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M12 19a7 7 0 110-14 7 7 0 010 14z"
              ></path>
            </svg>
            <span className="underline font-medium">
              What is my credit score?
            </span>
          </div> */}

            {/* Get Results Button */}
            <button
              type="button"
              className="bg-primary text-white w-full py-3 rounded-full font-semibold text-lg hover:opacity-95 transition"
              onClick={handleCalculate}
            >
              Get results
            </button>

            {/* <button
            type="button"
            className="bg-primary text-white w-full py-3 rounded-full font-semibold text-lg hover:opacity-95 transition"
            onClick={() => {
              if (!creditScore) {
                alert("Please select a credit score to see results");
              }
            }}
          >
            Get results
          </button> */}
          </div>

          {/* RIGHT SECTION - ESTIMATED RESULTS */}
          <div className="bg-[#f5f7ff] p-8 rounded-xl shadow">
            <h3 className="text-2xl font-bold text-black mb-4">
              Your estimated examples
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              These estimates are subject to credit checks, and may change if
              you do apply for finance.
            </p>

            {/* Car price + deposit */}
            <div className="bg-white rounded-lg p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-700 font-medium">Price of car</span>
                <span className="font-semibold text-primary">
                  £{carPrice.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">Total deposit</span>
                <span className="font-semibold text-primary">
                  £{deposit.toLocaleString()}
                </span>
              </div>
            </div>

            {/* PCP / HP comparison */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button
                onClick={() => setFinanceType("PCP")}
                className={`flex-1 bg-white rounded-lg p-4 border-2 transition cursor-pointer ${
                  financeType === "PCP" ? "border-primary" : "border-gray-200"
                }`}
              >
                <h3 className="text-primary font-bold text-xl mb-1">
                  £{financeType === "PCP" ? financeDetails.monthlyPayment.toFixed(2) : "---"}
                </h3>
                <p className="text-gray-700 font-medium">{financeType === "PCP" && termLength === 24 ? "per month lease purchase" : "per month (PCP)"}</p>
              </button>
              <button
                onClick={() => setFinanceType("HP")}
                className={`flex-1 bg-white rounded-lg p-4 border-2 transition cursor-pointer ${
                  financeType === "HP" ? "border-primary" : "border-gray-200"
                }`}
              >
                <h3 className="text-primary font-bold text-xl mb-1">
                  £{financeType === "HP" ? financeDetails.monthlyPayment.toFixed(2) : "---"}
                </h3>
                <p className="text-gray-700 font-medium">per month (HP)</p>
              </button>
            </div>

            {/* Payment details for selected finance type */}
            {(financeType === "HP" ? creditScore : true) ? (
              <div className="bg-white rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-primary font-semibold text-lg">
                    {financeType === "PCP" && termLength === 24 ? "24 monthly lease purchase payments of" : `${termLength} monthly payments of`}
                  </span>
                  <h4 className="text-2xl font-bold text-accent mb-4">
                    £{financeDetails.monthlyPayment.toFixed(2)}
                  </h4>
                </div>

                <p className="text-gray-600 text-sm mb-2">
                  Representative APR {financeType === "PCP" ? (termLength === 24 ? "11.9" : "8.5") : financeDetails.apr}%
                </p>

                <div className="flex justify-between mb-2">
                  <p className="text-gray-600 text-sm mb-1">
                    Amount of interest
                  </p>
                  <p className="text-gray-600 text-sm mb-1 font-bold">
                    £{financeDetails.totalInterest.toFixed(2)}
                  </p>
                </div>

                <div className="flex justify-between mb-2">
                  <div className="flex flex-col">
                    <p className="text-gray-600 text-sm mb-1">
                      Total Amount payable
                    </p>
                    <p className="text-gray-600 text-sm mb-1">
                      (including deposit)
                    </p>
                  </div>
                  <p className="text-gray-600 text-sm mb-1 font-bold">
                    £{financeDetails.totalPayable.toFixed(2)}
                  </p>
                </div>

                {/* PCP: Show balloon payment */}
                {financeType === "PCP" && (
                  <div className="flex justify-between mb-2">
                    <p className="text-gray-600 text-sm mb-1">
                      Optional final payment (balloon)
                    </p>
                    <p className="text-gray-600 text-sm mb-1 font-bold">
                      £{financeDetails.balloon ? financeDetails.balloon.toFixed(2) : "0.00"}
                    </p>
                  </div>
                )}

                <div className="border border-primary rounded-lg p-3 mb-4 text-center">
                  <h5 className="font-semibold mb-1 text-primary">
                    {financeType === "PCP" && termLength === 24
                      ? "Lease Purchase"
                      : financeType === "HP"
                        ? "Hire Purchase (HP)"
                        : "Personal Contract Purchase (PCP)"}
                  </h5>
                  <p className="text-gray-600 text-sm mb-2">
                    {financeType === "HP"
                      ? "Usually has higher monthly payments compared to PCP, but the car will belong to you at the end of your finance term."
                      : "Finance figures are for illustration only. Actual finance terms, APR and Guaranteed Future Value may vary depending on lender, vehicle, mileage and customer profile."}
                  </p>
                </div>

                <button
                  className={`bg-accent text-white w-full py-3 rounded-full font-semibold hover:opacity-95 transition ${
                    isSold
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-accent text-white hover:opacity-90"
                  }`}
                  disabled={isSold}
                  onClick={() => {
                    if (financeType === "HP" && !creditScore) {
                      alert("Please select a credit score to see results");
                      return;
                    }

                    // Collect data to pass
                    const selectedFinance = {
                      deposit,
                      annualMileage,
                      termLength,
                      creditScore: financeType === "HP" ? creditScore : "fair",
                      financeType,
                      monthlyPayment: financeDetails.monthlyPayment,
                      totalInterest: financeDetails.totalInterest,
                      totalPayable: financeDetails.totalPayable,
                      balloon: financeDetails.balloon,
                    };

                    if (typeof onRequestFinance === "function") {
                      onRequestFinance(selectedFinance);
                    }
                  }}
                >
                  Request Finance
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-gray-500 text-sm">
                  Please select a credit score to see your finance estimates
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
);

export default FinanceCalculator;
